function finite(value,fallback=0){const n=Number(value);return Number.isFinite(n)?n:fallback;}
function clamp(value,min,max){return Math.max(min,Math.min(max,value));}

// Canonical motherboard component-side reference frame used by the simulator and solver.
//
// This is the chirality-preserving port of the cleaned motherboard reference into the
// simulator's X/Z PCB plane. The source model is X=visual right, Y=top, Z=component side.
// Rotating it into X/Z while keeping component side on local +Y requires source X -> local -X.
//
// Local axes therefore are:
//   +X = physical LEFT / rear-I/O edge
//   -X = physical RIGHT / DIMM edge
//   +Z = physical TOP/EPS edge
//   -Z = primary-PCIe/bottom edge
//   +Y = PCB normal / component side
//
// This sign change is deliberate: copying source X directly into local X is a reflection,
// which is why the detailed board previously looked horizontally mirrored in real cases.
// DTX inherits the ITX upper-board standard and ATX inherits mATX; larger form factors extend
// the outline without independently moving CPU/DIMM/PCIe landmarks.
//
// Offsets are stored from physical LEFT/TOP edges so non-nominal board outlines still retain
// the correct relationship instead of being re-scaled around the board centre.
const PROFILE = Object.freeze({
  ITX:Object.freeze({
    socketFromLeft:91.69,
    socketFromTop:69.43,
    ramSlotFromLeft:Object.freeze([143.70,153.00]), // inner -> outer
    ramCenterFromTop:80.675,
    pcieCenterFromLeft:88.175,
    pcieFromTop:165.00,
    pcieSpan:89.00,
    ramPitch:9.30
  }),
  DTX:Object.freeze({
    socketFromLeft:91.69,
    socketFromTop:69.43,
    ramSlotFromLeft:Object.freeze([143.70,153.00]),
    ramCenterFromTop:80.675,
    pcieCenterFromLeft:88.175,
    pcieFromTop:165.00,
    pcieSpan:89.00,
    ramPitch:9.30
  }),
  MATX:Object.freeze({
    socketFromLeft:128.93,
    socketFromTop:90.98,
    ramSlotFromLeft:Object.freeze([176.42,187.42,198.42,209.42]), // CPU-side -> outer
    // Desktop boards grow mainly below the CPU/DIMM zone. Keep the DIMM bank on the same
    // physical top-edge datum as ITX/DTX instead of pushing it ~15 mm toward the primary PCIe
    // slot simply because the PCB outline is taller. Slot count/pitch still remain form-factor specific.
    ramCenterFromTop:80.675,
    pcieCenterFromLeft:88.175,
    pcieFromTop:165.00,
    pcieSpan:89.00,
    ramPitch:11.00
  }),
  ATX:Object.freeze({
    socketFromLeft:128.93,
    socketFromTop:90.98,
    ramSlotFromLeft:Object.freeze([176.42,187.42,198.42,209.42]),
    ramCenterFromTop:80.675,
    pcieCenterFromLeft:88.175,
    pcieFromTop:165.00,
    pcieSpan:89.00,
    ramPitch:11.00
  })
});

export function normalizeMotherboardFormFactor(value='ITX'){
  const raw=String(value||'ITX').toUpperCase().replace(/MICRO[-_ ]?ATX|M[-_ ]?ATX/g,'MATX').replace(/MINI[-_ ]?ITX/g,'ITX');
  if(raw.includes('MATX'))return 'MATX';
  if(raw.includes('DTX'))return 'DTX';
  if(raw.includes('ATX')&&!raw.includes('ITX'))return 'ATX';
  return 'ITX';
}

export function defaultMotherboardRamSlotCount(formFactor='ITX'){
  const ff=normalizeMotherboardFormFactor(formFactor);
  return (ff==='MATX'||ff==='ATX')?4:2;
}

function sourceXFromLeft(offset,half,margin=0){
  return clamp(-half+offset,-half-margin,half+margin);
}
function localXFromLeft(offset,half,margin=0){
  return -sourceXFromLeft(offset,half,margin);
}
function fromTop(offset,half,margin=0){
  return clamp(half-offset,-half-margin,half+margin);
}

export function motherboardStandardLandmarks(dims={},formFactor='ITX'){
  const l=Math.max(80,finite(dims?.l,170)||170);
  const w=Math.max(80,finite(dims?.w,170)||170);
  const ff=normalizeMotherboardFormFactor(formFactor);
  const p=PROFILE[ff]||PROFILE.ITX;
  const halfW=w/2,halfL=l/2;

  const socketX=localXFromLeft(p.socketFromLeft,halfW,0);
  const socketZ=fromTop(p.socketFromTop,halfL,0);
  const ramSlotXs=p.ramSlotFromLeft.map(offset=>localXFromLeft(offset,halfW,0));
  const ramX=ramSlotXs.reduce((sum,x)=>sum+x,0)/Math.max(1,ramSlotXs.length);
  const ramZ=fromTop(p.ramCenterFromTop,halfL,0);

  // The cleaned reference places the primary x16 connector centre 165 mm from the top edge
  // on both ITX and mATX. Its 89 mm body is allowed the same slight edge overhang as a real
  // connector, so do not clamp the span back inside the PCB outline.
  const pcieCenterX=localXFromLeft(p.pcieCenterFromLeft,halfW,1);
  const pcieZ=fromTop(p.pcieFromTop,halfL,1);
  const pcieSpan=Math.min(p.pcieSpan,Math.max(55,w-20));
  const pcieStartX=pcieCenterX-pcieSpan/2;
  const pcieEndX=pcieCenterX+pcieSpan/2;

  return {
    frame:'board_standard_reference_v7_pcie_face_anchor',
    formFactor:ff,
    socketX,
    socketZ,
    ramX,
    ramZ,
    ramSlotXs,
    ramPitch:p.ramPitch,
    pcieZ,
    pcieStartX,
    pcieEndX,
    pcieCenterX,
    pcieSpan,
    sourceOffsets:{
      socketFromLeft:p.socketFromLeft,
      socketFromTop:p.socketFromTop,
      ramCenterFromTop:p.ramCenterFromTop,
      ramSlotFromLeft:[...p.ramSlotFromLeft],
      pcieCenterFromLeft:p.pcieCenterFromLeft,
      pcieFromTop:p.pcieFromTop,
      sourceToLocal:'source_x_to_local_negative_x'
    },
    canonical:{rearIoEdge:'+X',dimmSide:'-X',primaryPcieSide:'-Z',topEdge:'+Z',componentSide:'+Y'}
  };
}

export function socketLocalForMotherboardStandard(dims={},formFactor='ITX',socketY=0){
  const m=motherboardStandardLandmarks(dims,formFactor);
  return [m.socketX,finite(socketY,0),m.socketZ];
}

// Primary x16 connector reference used by reference-layout GPU registration. The motherboard
// remains the spatial authority: larger MATX/ATX boards therefore move the GPU to the real
// slot height instead of treating the board's bottom edge as the connector.
export function pcieLocalForMotherboardStandard(dims={},formFactor='ITX',pcieY=0){
  const m=motherboardStandardLandmarks(dims,formFactor);
  return [m.pcieCenterX,finite(pcieY,0),m.pcieZ];
}
